import React, { useState, useEffect, useRef } from 'react';
import StrangerThingsAuto from './StrangerThingsAuto';
import './StrangerThingshero.css';

const StrangerThingsHero = () => {
    const [introHidden, setIntroHidden] = useState(false);
    const [viewportVisible, setViewportVisible] = useState(false);
    const [letterboxVisible, setLetterboxVisible] = useState(false);
    const [activeScene, setActiveScene] = useState(null);
    const [showFullTitle, setShowFullTitle] = useState(false);
    const [visibleCredits, setVisibleCredits] = useState([]);
    const [showFinalCredit, setShowFinalCredit] = useState(false);
    const [browserCompatible, setBrowserCompatible] = useState('can');

    const [showLightning, setShowLightning] = useState(false);


    const audioRef = useRef(null);
    const started = useRef(false);

    useEffect(() => {
        // Simple browser compatibility check
        const checkBrowserCompatibility = () => {
            // Check for basic CSS animation support
            const testEl = document.createElement('div');
            const hasAnimations = 'animation' in testEl.style || 'webkitAnimation' in testEl.style;
            const hasTextStroke = 'webkitTextStroke' in testEl.style || 'textStroke' in testEl.style;

            if (!hasAnimations) {
                setBrowserCompatible('cant');
            } else if (!hasTextStroke) {
                setBrowserCompatible('shouldnt');
            } else {
                setBrowserCompatible('can');
                // Auto start if compatible
                startAnimation();
            }
        };

        checkBrowserCompatibility();
    }, []);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const tryPlay = () => {
            audio.play().catch(() => { });
            window.removeEventListener("mousemove", tryPlay);
            window.removeEventListener("touchstart", tryPlay);
        };

        window.addEventListener("mousemove", tryPlay);
        window.addEventListener("touchstart", tryPlay);

        return () => {
            window.removeEventListener("mousemove", tryPlay);
            window.removeEventListener("touchstart", tryPlay);
        };
    }, []);

    // Configuration constants for lightning
    const enableLightningStrikes = true;

    const startAnimation = () => {
        if (started.current) return;
        started.current = true;

        // Hide intro immediately
        setIntroHidden(true);

        // Show viewport and letterbox faster
        setTimeout(() => {
            setViewportVisible(true);
            setLetterboxVisible(true);
            startSceneSequence();
        }, 500); // Reduced from 1500 for faster start
    };

    const startSceneSequence = () => {
        const creditsMs = 3000;
        const scenesMs = [
            creditsMs,
            creditsMs * 2,
            creditsMs,
            creditsMs,
            creditsMs,
            creditsMs,
            creditsMs * 2,
            19500,
        ];

        const credits = [
            { id: 0 },
            { id: 1 },
            { id: 2 },
            { id: 3 },
            { id: 4 },
            { id: 5 },
            { id: 6 },
            { id: 7 },
            { id: 8 },
            { id: 9 },
            { id: 10 },
            { id: 11 },
            { id: 12 },
        ];

        // Show credits in sequence
        credits.forEach((credit, i) => {
            setTimeout(() => {
                setVisibleCredits(prev => {
                    const newVisible = prev.filter(id => id !== i - 1);
                    return [...newVisible, i];
                });
            }, i * creditsMs);

            if (i === credits.length - 1) {
                setTimeout(() => {
                    setVisibleCredits(prev => prev.filter(id => id !== i));
                }, i * creditsMs + creditsMs);
            }
        });

        // Show scenes in sequence
        let offset = 0;
        for (let i = 0; i < 7; i++) {
            setTimeout(() => {
                setActiveScene(i);
            }, offset);

            offset += scenesMs[i];

            if (i === 6) {
                // Show full title
                setTimeout(() => {
                    setActiveScene(null);
                    setShowFullTitle(true);
                }, offset);

                // Show final credits
                setTimeout(() => {
                    setShowFinalCredit(true);
                }, offset + scenesMs[7] + 1500);
            }
        }

        // Optional: Play audio
        if (audioRef.current) {
            setTimeout(() => {
                audioRef.current.play().catch(err => {
                    console.log('Audio playback prevented:', err);
                });
            }, 1700);
        }
    };

    const creditsData = [
        [{ text: 'A Netflix Original Series' }],
        [{ text: 'Winona Ryder', attr: 'Winona Ryder' }],
        [{ text: 'David Harbour' }],
        [
            { text: 'Finn Wolfhard', attr: 'Finn Wolfhard' },
            { text: 'Millie Bobby Brown', attr: 'Millie Bobby Brown' }
        ],
        [
            { text: 'Gaten Matarazzo', attr: 'Gaten Matarazzo' },
            { text: 'Caleb McLaughlin', attr: 'Caleb McLaughlin' }
        ],
        [
            { text: 'Natalia Dyer', attr: 'Natalia Dyer' },
            { text: 'Charlie Heaton', attr: 'Charlie Heaton' }
        ],
        [{ text: 'Cara Buono', attr: 'Cara Buono' }],
        [
            { text: 'And', attr: 'And', sub: true },
            { text: 'Matthew Modine', attr: 'Matthew Modine' }
        ],
        // [
        //     { text: 'Co-Executive Producer', attr: 'Co-Executive Producer', sub: true },
        //     { text: 'Iain Paterson', attr: 'Iain Paterson' }
        // ],
        // [
        //     { text: 'Executive Producer', attr: 'Executive Producer', sub: true },
        //     { text: 'Karl Gajdusek', attr: 'Karl Gajdusek' }
        // ],
        // [
        //     { text: 'Executive Producers', attr: 'Executive Producers', sub: true },
        //     { text: 'Cindy Holland', attr: 'Cindy Holland' },
        //     { text: 'Brian Wright', attr: 'Brian Wright' },
        //     { text: 'Matt Thunell', attr: 'Matt Thunell' }
        // ],
        // [
        //     { text: 'Executive Producers', attr: 'Executive Producers', sub: true },
        //     { text: 'Shawn Levy', attr: 'Shawn Levy' },
        //     { text: 'Dan Cohen', attr: 'Dan Cohen' }
        // ],
        // [
        //     { text: 'Executive Producers', attr: 'Executive Producers', sub: true },
        //     { text: 'The Duffer Brothers', attr: 'The Duffer Brothers' }
        // ],
    ];

    return (
        <div className="stranger-things-hero-wrapper">
            <StrangerThingsAuto
                className="sth-hero-background"
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                numEffects={3}
                trailLength={30}
                grainIntensity={0.02}
                bloomStrength={0.4}
                bloomRadius={0.6}
                brightness={0.9}
                color="#C11B1F"
                enableLightningStrikes={showFullTitle}
                lightningInterval={3000}
                lightningDuration={200}
                lightningIntensity={3.5}
                zIndex={0}
                enableMouseTracking={true}
                showLogo={false}
            />

            {/* Intro Screen */}
            <div className={`sth-intro ${introHidden ? 'sth-intro--hide' : ''}`} style={{ zIndex: 20, position: 'fixed' }}>
                {/* <h1 className="sth-intro-title">Stranger Things</h1> */}

                {browserCompatible === 'can' && (
                    <p className="sth-intro-text sth-intro-text--show">
                        {/* The following demo is performance intensive, and will play audio. If you
                        experience any issues, try sizing down your browser and refreshing.
                        <br />*/}
                        {/* <button className="sth-intro-text-play" onClick={startAnimation}>
                            &#9654;
                        </button> */}
                    </p>
                )}

                {browserCompatible === 'shouldnt' && (
                    <p className="sth-intro-text sth-intro-text--show">
                        Unfortunately this demo uses many features only found in the latest
                        version of Google Chrome. You can still run this, but it may not look
                        as intended. Please try running it in Chrome for the best experience.
                        <br />
                        <a className="sth-intro-text-button" href="https://www.google.com/chrome/browser/">
                            Download Chrome
                        </a>
                        <button className="sth-intro-text-button" onClick={startAnimation}>
                            Play Anyway
                        </button>
                    </p>
                )}

                {browserCompatible === 'cant' && (
                    <p className="sth-intro-text sth-intro-text--show">
                        Unfortunately your browser doesn't support this demo. Please download
                        Google Chrome, and try running it again.
                        <br />
                        <a className="sth-intro-text-button" href="https://www.google.com/chrome/browser/">
                            Download Chrome
                        </a>
                    </p>
                )}
            </div>

            {/* Main Viewport */}
            <div className={`sth-viewport ${viewportVisible ? 'sth-viewport--show' : ''}`} style={{ zIndex: 10, position: 'relative' }}>
                <div className="sth-scene">
                    {/* Full Title */}
                    <div className={`sth-title sth-title--full ${showFullTitle ? 'sth-title--show' : ''}`}>
                        <div className="sth-title-word">
                            <span className="sth-title-word-letter" data-letter="S1">
                                <span className="sth-title-word-letter-large">S</span>
                                <div className="sth-titlebar sth-titlebar--left"></div>
                            </span>
                            <span className="sth-title-word-letter" data-letter="T1">T</span>
                            <span className="sth-title-word-letter" data-letter="R1">R</span>
                            <span className="sth-title-word-letter" data-letter="A">A</span>
                            <span className="sth-title-word-letter" data-letter="N1">N</span>
                            <span className="sth-title-word-letter" data-letter="G1">G</span>
                            <span className="sth-title-word-letter" data-letter="E">E</span>
                            <span className="sth-title-word-letter" data-letter="R2">
                                <span className="sth-title-word-letter-large">R</span>
                                <div className="sth-titlebar sth-titlebar--right"></div>
                            </span>
                            <div className="sth-titlebar sth-titlebar--top"></div>
                        </div>

                        <div></div>

                        <div className="sth-title-word sth-title-word--second">
                            <span className="sth-title-word-letter" data-letter="T2">T</span>
                            <span className="sth-title-word-letter" data-letter="H">H</span>
                            <span className="sth-title-word-letter" data-letter="I">I</span>
                            <span className="sth-title-word-letter" data-letter="N2">N</span>
                            <span className="sth-title-word-letter" data-letter="G2">G</span>
                            <span className="sth-title-word-letter" data-letter="S2">S</span>
                        </div>
                    </div>

                    {/* Scene 0 - Empty */}
                    <div className={`sth-title sth-title--scene sth-title--scene0 ${activeScene === 0 ? 'sth-title--show' : ''}`}></div>

                    {/* Scene 1 - Letter A */}
                    <div className={`sth-title sth-title--scene sth-title--scene1 ${activeScene === 1 ? 'sth-title--show' : ''}`}>
                        <div className="sth-title-word">
                            <div className="sth-title-word-letter" data-letter="A">A</div>
                        </div>
                    </div>

                    {/* Scene 2 - Letter N */}
                    <div className={`sth-title sth-title--scene sth-title--scene2 ${activeScene === 2 ? 'sth-title--show' : ''}`}>
                        <div className="sth-title-word">
                            <div className="sth-title-word-letter" data-letter="N">N</div>
                        </div>
                    </div>

                    {/* Scene 3 - Letters R I */}
                    <div className={`sth-title sth-title--scene sth-title--scene3 ${activeScene === 3 ? 'sth-title--show' : ''}`}>
                        <div className="sth-title-word">
                            <div className="sth-title-word-letter" data-letter="R">R</div>
                            <div className="sth-title-word-letter" data-letter="I">I</div>
                        </div>
                    </div>

                    {/* Scene 4 - Letters S G */}
                    <div className={`sth-title sth-title--scene sth-title--scene4 ${activeScene === 4 ? 'sth-title--show' : ''}`}>
                        <div className="sth-title-word">
                            <div className="sth-title-word-letter" data-letter="S">S</div>
                            <div className="sth-title-word-letter" data-letter="G">G</div>
                        </div>
                    </div>

                    {/* Scene 5 - Letters R S */}
                    <div className={`sth-title sth-title--scene sth-title--scene5 ${activeScene === 5 ? 'sth-title--show' : ''}`}>
                        <div className="sth-title-word">
                            <div className="sth-title-word-letter" data-letter="R">R</div>
                            <div className="sth-title-word-letter" data-letter="S">S</div>
                        </div>
                    </div>

                    {/* Scene 6 - Letters I A T H N G */}
                    <div className={`sth-title sth-title--scene sth-title--scene6 ${activeScene === 6 ? 'sth-title--show' : ''}`}>
                        <div className="sth-title-word">
                            <div className="sth-title-word-letter" data-letter="I">I</div>
                            <div className="sth-title-word-letter" data-letter="A">A</div>
                            <div className="sth-title-word-letter" data-letter="T">T</div>
                            <div className="sth-title-word-letter" data-letter="H">H</div>
                            <div className="sth-title-word-letter" data-letter="N">N</div>
                            <div className="sth-title-word-letter" data-letter="G">G</div>
                        </div>
                    </div>
                </div>

                {/* Credits */}
                <div className="sth-credits">
                    {creditsData.map((creditGroup, index) => (
                        <div
                            key={index}
                            className={`sth-credits-group ${visibleCredits.includes(index) ? 'sth-credits-group--show' : ''}`}
                        >
                            {creditGroup.map((credit, i) => (
                                <div
                                    key={i}
                                    className={credit.sub ? 'sth-credits-group-sub' : 'sth-credits-group-credit'}
                                    data-text={credit.attr || credit.text}
                                >
                                    {credit.html ? (
                                        <span dangerouslySetInnerHTML={{ __html: credit.html }} />
                                    ) : (
                                        credit.text
                                    )}
                                </div>
                            ))}
                        </div>
                    ))}

                    {/* Final Credits */}
                    {/* <div className={`sth-credits-final ${showFinalCredit ? 'sth-credits-group--show' : ''}`}>
                        <div className="sth-credits-group-sub" data-text="Created By">
                            Created By
                        </div>
                        <div className="sth-credits-group-credit" data-text="The Duffer Brothers">
                            The Duffer Brothers
                        </div>

                        {/* <a
                            href="https://github.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="sth-credits-final-link sth-credits-final-link--github"
                            aria-label="GitHub"
                        ></a>
                        <a
                            href="https://twitter.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="sth-credits-final-link sth-credits-final-link--twitter"
                            aria-label="Twitter"
                        ></a>
                        <a
                            href="https://codepen.io"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="sth-credits-final-link sth-credits-final-link--codepen"
                            aria-label="CodePen"
                        ></a> 
                    </div> */}
                </div>
            </div>

            {/* Visual Effects */}
            <div className="sth-vignette"></div>
            <div className="sth-grain"></div>

            {/* Letterbox */}
            <div className={`sth-letterbox ${letterboxVisible ? 'sth-letterbox--show' : ''}`}>
                <div className="sth-letterbox-cover sth-letterbox-cover--top"></div>
                <div className="sth-letterbox-cover sth-letterbox-cover--left"></div>
                <div className="sth-letterbox-cover sth-letterbox-cover--right"></div>
                <div className="sth-letterbox-cover sth-letterbox-cover--bottom"></div>
            </div>

            {/* Audio (optional) */}
            <audio
                ref={audioRef}
                src="https://s3-us-west-2.amazonaws.com/s.cdpn.io/161676/music.mp3"
                preload="auto"
            />
        </div>
    );
};

export default StrangerThingsHero;
